"use client";

// useDropConfirm — the reusable "really move it here?" confirmation for any
// folder-management surface (free scatter · orbit · grid). While a dragged item
// DWELLS over a valid target, an inner glow grows from the target's border inward
// (blurred → solid) over `holdMs`. That growth IS the confirmation: it materializes
// to imply "this is where the thing will land". A release after the glow has taken
// hold (past `gate`) commits the move; a release on a mere graze does not.
//
// The controller is imperative — it toggles a `data-drop-target` attribute and
// animates a `--drop-progress` (0→1) CSS var on the target element, so a 60fps
// drag never re-renders React. The CSS in globals.css (`[data-drop-target]`) reads
// `--drop-progress` to draw the materializing glow. One controller per dragged
// item; only the active drag runs its rAF.

import { useEffect, useRef } from "react";

export interface DropConfirm {
  /** Light a target element (or clear the current one with null). Idempotent. */
  point: (el: HTMLElement | null) => void;
  /** True once the lit target has dwelled past the gate — a release should commit. */
  armed: () => boolean;
  /** The element currently lit (or null). */
  target: () => HTMLElement | null;
  /** Tear everything down (drag ended / cancelled). */
  reset: () => void;
}

export function useDropConfirm(opts?: { holdMs?: number; gate?: number }): DropConfirm {
  const holdMs = opts?.holdMs ?? 1000; // border → solid-center duration
  const gate = opts?.gate ?? 0.16; // min progress before a release commits (anti-graze)

  const elRef = useRef<HTMLElement | null>(null);
  const progress = useRef(0);
  const startAt = useRef(0);
  const raf = useRef<number | null>(null);
  const running = useRef(false);

  const write = (p: number) => {
    elRef.current?.style.setProperty("--drop-progress", p.toFixed(3));
  };
  const stop = () => {
    running.current = false;
    if (raf.current != null) {
      cancelAnimationFrame(raf.current);
      raf.current = null;
    }
  };
  const clear = (el: HTMLElement | null) => {
    if (!el) return;
    el.removeAttribute("data-drop-target");
    el.style.removeProperty("--drop-progress");
  };

  const loop = (now: number) => {
    if (!running.current) return;
    const p = Math.min(1, (now - startAt.current) / holdMs);
    progress.current = p;
    write(p);
    if (p < 1) raf.current = requestAnimationFrame(loop);
    else running.current = false; // reached solid; hold it lit
  };

  const point = (el: HTMLElement | null) => {
    if (el === elRef.current) return;
    clear(elRef.current);
    stop();
    elRef.current = el;
    progress.current = 0;
    if (el) {
      el.setAttribute("data-drop-target", "true");
      write(0);
      startAt.current = performance.now();
      running.current = true;
      raf.current = requestAnimationFrame(loop);
    }
  };

  const reset = () => {
    clear(elRef.current);
    stop();
    elRef.current = null;
    progress.current = 0;
  };

  // clean up if the host unmounts mid-drag
  useEffect(() => () => reset(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    point,
    armed: () => progress.current >= gate,
    target: () => elRef.current,
    reset,
  };
}
